import type { RegistryNode, NodeConfig } from "./types";
import { isEntityConfig } from "./types";

/** URL prefix for config-driven back-office routes (e.g. `/v2/settings/metadata/categories`). */
export const BACKOFFICE_V2_PREFIX = "/v2";

const ROOT_PREFIX = "/src/v2/backoffice/";
const INDEX_SUFFIX = "/index.ts";

const modules = import.meta.glob<{ default?: NodeConfig }>("/src/v2/backoffice/**/index.ts", {
  eager: true,
});

const toNode = (filePath: string, config: NodeConfig): RegistryNode => {
  const rel = filePath.slice(ROOT_PREFIX.length, -INDEX_SUFFIX.length);
  const segments = rel.split("/");
  return {
    segment: segments[segments.length - 1]!,
    path: "/" + rel,
    config,
    children: [],
  };
};

const sortByPosition = (nodes: RegistryNode[]): RegistryNode[] =>
  [...nodes]
    .sort((a, b) => (a.config.position ?? 0) - (b.config.position ?? 0))
    .map((n) => ({ ...n, children: sortByPosition(n.children) }));

const buildTree = (): RegistryNode[] => {
  const nodeMap = new Map<string, RegistryNode>();

  for (const [filePath, mod] of Object.entries(modules)) {
    const config = mod.default;
    const rel = filePath.slice(ROOT_PREFIX.length, -INDEX_SUFFIX.length);
    if (!config?.name || !rel) continue;
    nodeMap.set(rel, toNode(filePath, config));
  }

  const roots: RegistryNode[] = [];

  nodeMap.forEach((node, rel) => {
    const slash = rel.lastIndexOf("/");
    const parentRel = slash === -1 ? "" : rel.slice(0, slash);
    const parent = parentRel ? nodeMap.get(parentRel) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  });

  return sortByPosition(roots);
};

const find = (nodes: RegistryNode[], path: string): RegistryNode | undefined => {
  for (const node of nodes) {
    if (node.path === path) return node;
    const found = find(node.children, path);
    if (found) return found;
  }
};

const resolvePath = (path: string): RegistryNode | undefined => {
  const normalized =
    path === BACKOFFICE_V2_PREFIX || path.startsWith(`${BACKOFFICE_V2_PREFIX}/`)
      ? path.slice(BACKOFFICE_V2_PREFIX.length) || "/"
      : path;

  const exact = find(sections, normalized);
  if (exact) return exact;

  const lastSlash = normalized.lastIndexOf("/");
  if (lastSlash <= 0) return undefined;

  const parent = find(sections, normalized.slice(0, lastSlash));
  if (parent && isEntityConfig(parent.config)) return parent;
  return undefined;
};

const collectEntities = (nodes: RegistryNode[]): RegistryNode[] =>
  nodes.flatMap((n) =>
    isEntityConfig(n.config) ? [n, ...collectEntities(n.children)] : collectEntities(n.children),
  );

const sections = buildTree();

export const backofficeRegistry = {
  sections,
  resolve: (path: string) => resolvePath(path),
  get entities() {
    return collectEntities(sections);
  },
};
